/**
 * External dependencies
 */
import React from "react";
import { Box, Text } from "@towersystems/towersystems-design-system";
import { find, capitalize, get } from "lodash";
import classnames from "classnames";

/**
 * Internal dependencies
 */
import {
  toTablePivotFormatter,
  getPercentageChange,
  isDateLikeString,
  resultSetToTimeZone,
} from "./utils";
import Icon from "components/icon";

/**
 * Style dependencies
 */
import "./numbers.scss";

const NumberRenderer = ({ resultSet, name, compare }) => {
  const columnConfig = resultSet.tableColumns();
  const timezone = resultSetToTimeZone(resultSet);

  // Render the compare value
  const renderCompare = (value1, value2) => {
    if (!compare) {
      return null;
    }

    const v = getPercentageChange(value1, value2);

    if (v === 0) {
      return "";
    }

    let normalizedNumber = parseFloat(v);

    if (isNaN(normalizedNumber)) {
      return "";
    }

    let formatted = Math.ceil(
      Math.round((normalizedNumber + Number.EPSILON) * 100) / 100
    );

    let isNegative = formatted <= 0;
    let classes = classnames(
      "report__variance",
      { "is-negative": isNegative },
      { "is-positive": !isNegative }
    );

    return (
      <Box display="flex" alignItems="center" className={classes}>
        <span className="report__variance-icon">
          {isNegative ? <Icon name="FaCaretDown" /> : <Icon name="FaCaretUp" />}
        </span>
        <span>{Math.abs(formatted)}</span>
        <span>%</span>
      </Box>
    );
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      width="full"
    >
      <Text className="charts__numbers-single-number-label">{name}</Text>
      <Box className="charts__numbers">
        {resultSet.pivot().map((data, i) => {
          const xValues = get(data, "xValues", []);
          const yValuesArray = get(data, "yValuesArray", []);

          const dataKey =
            xValues.length == 0
              ? get(yValuesArray, [0, 0, 0], "")
              : get(data, "xValues", []).join("-");

          const rowData = get(data, "yValuesArray", ["", 0])[0];

          const compareRowData = get(data, "yValuesArray", undefined)?.[1];
          const valueKey = rowData[0][0];

          let config = find(columnConfig, { key: dataKey });

          if (config && !config.type) {
            config = find(config.children, { key: valueKey });
          }

          let formatter = toTablePivotFormatter(config);

          const categoryLabel = isDateLikeString(data.xValues[0] ?? "Unknown")
            ? data.xValues.slice(1).join(" ")
            : data.xValues.join(" ");

          return (
            <Box key={`${dataKey}-${i}`} className="charts__numbers-inner">
              <Box
                display="flex"
                alignItems="center"
                flexDirection="row"
                className="charts__numbers-number"
              >
                <Box>
                  {categoryLabel ? (
                    <Text
                      align="center"
                      size="small"
                      className="charts__numbers-number-category"
                    >
                      {capitalize(categoryLabel)}
                    </Text>
                  ) : undefined}
                </Box>
                <Box display="flex" alignItems="center">
                  <Text
                    align="center"
                    size="large"
                    className="charts__numbers-single-number-value"
                  >
                    {(formatter && formatter(rowData[1], {}, { timezone })) ||
                      0}
                  </Text>
                  {compareRowData
                    ? renderCompare(rowData[1], compareRowData[1])
                    : undefined}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default NumberRenderer;
